import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Container, Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../models/AuthContext';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection } from 'firebase/firestore';
import { db } from '../config/firebase';
import axios from 'axios';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  maxWidth: 600,
  margin: 'auto',
  marginTop: theme.spacing(4),
}));

const FUNCTIONS_BASE_URL = 'https://us-central1-theapp-428906.cloudfunctions.net';

const GoogleAuth = () => {
  const [hasGmailAccess, setHasGmailAccess] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const auth = getAuth();

  useEffect(() => {
    const checkGmailAccess = async () => {
      console.log(currentUser);
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        console.log(userDoc);
        if (!userDoc.exists()) {
          setHasGmailAccess(false);
          setError(null);
          setLoading(false);
          return;
        }

        const gmailAuth = userDoc.data()?.gmailAuth;
        if (!gmailAuth?.accessToken) {
          setHasGmailAccess(false);
        } else {
          // Vérifier si le token n'est pas expiré
          const expiresAt = gmailAuth.expiresAt?.toDate();
          const isExpired = expiresAt && expiresAt < new Date();
          
          if (isExpired) {
            // Si le token est expiré, on considère qu'on n'a pas l'accès
            setHasGmailAccess(false);
          } else {
            setHasGmailAccess(true);
          }
        }
        setError(null);
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'accès Gmail:', error);
        setError('Erreur lors de la vérification de l\'accès Gmail');
        setHasGmailAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkGmailAccess();
  }, [currentUser, db]);

  const handleGoogleAuth = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = window.location.origin + '/GoogleAuth';
    const scope = 'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.modify';
    
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
    
    window.location.href = authUrl;
  };

  const handleRevokeAccess = async () => {
    if (!currentUser) {
      setError('Vous devez être connecté pour révoquer l\'accès');
      return;
    }

    try {
      setLoading(true);
      const idToken = await auth.currentUser.getIdToken();
      await axios.post(`${FUNCTIONS_BASE_URL}/revokeGmailAccess`, {}, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      setHasGmailAccess(false);
      setError(null);
    } catch (error) {
      console.error('Erreur lors de la révocation de l\'accès:', error);
      setError('Erreur lors de la révocation de l\'accès Gmail');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleAuthCode = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code && currentUser) {
        try {
          setLoading(true);
          const idToken = await auth.currentUser.getIdToken();
          await axios.post(`${FUNCTIONS_BASE_URL}/exchangeGmailCode`, {
            code,
            redirectUri: window.location.origin + '/GoogleAuth'
          }, {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          });
          setHasGmailAccess(true);
          setError(null);
          // Nettoyer l'URL
          window.history.replaceState({}, document.title, '/GoogleAuth');
        } catch (error) {
          console.error('Erreur lors de l\'échange du code:', error);
          setError('Erreur lors de l\'autorisation Gmail');
          setHasGmailAccess(false);
        } finally {
          setLoading(false);
        }
      } else if (!currentUser) {
        setLoading(false);
      }
    };

    handleAuthCode();
  }, [currentUser, auth]);

  if (!currentUser) {
    return (
      <Container>
        <StyledPaper elevation={3}>
          <Typography variant="h6" gutterBottom>
            Veuillez vous connecter pour accéder à cette page
          </Typography>
        </StyledPaper>
      </Container>
    );
  }

  return (
    <Container>
      <StyledPaper elevation={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Autorisation Gmail
        </Typography>

        {error && (
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
        )}

        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              {hasGmailAccess 
                ? "Vous avez autorisé l'accès à votre compte Gmail"
                : "Pour permettre à l'application d'accéder à vos emails, vous devez autoriser l'accès à votre compte Gmail"}
            </Typography>

            {!hasGmailAccess ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleGoogleAuth}
                sx={{ mt: 2 }}
              >
                Autoriser l'accès Gmail
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleRevokeAccess}
                sx={{ mt: 2 }}
              >
                Révoquer l'accès
              </Button>
            )}
          </>
        )}
      </StyledPaper>
    </Container>
  );
};

export default GoogleAuth;
